<template>
	<div class="field has-addons mb-0">
		<div
			class="control is-expanded"
			:class="{ 'has-icons-right': detailsValue.type === 'PERCENTAGE', 'is-loading': loadingSuggestions }"
		>
			<neko-select
				v-if="detailsValue.type === 'BOOLEAN'"
				:value="detailsValue.value != null ? parseInt(detailsValue.value, 10) : null"
				@input="onInput($event, autoValidate)"
			>
				<option :value="1" v-t="'boolean.true'"></option>
				<option :value="0" v-t="'boolean.false'"></option>
			</neko-select>
			<suggestion-select
				v-else-if="suggestions"
				:loading="loadingSuggestions"
				:suggestions="suggestions"
				:show-key="detailsValue.code === 'idcc' || detailsValue.code === 'cog'"
				:value="detailsValue.value"
				@input="onInput($event)"
			/>
			<input
				v-else
				:placeholder="detailsValue.label"
				class="input"
				:disabled="loadingSuggestions"
				step="0.0001"
				:type="getTypeInput(detailsValue)"
				:value="detailsValue.type !== 'DATE' ? detailsValue.value : formatDateForInput(detailsValue.value)"
				@input="onInput($event)"
				@keypress.enter="onValidate"
			/>
			<span v-if="detailsValue.type === 'PERCENTAGE'" class="icon is-right">
				<span class="fas fa-percent"></span>
			</span>
		</div>
		<div class="control">
			<button @click.prevent="onDelete" class="button is-outlined is-danger">
				<span class="icon"><span class="fas fa-trash"></span></span>
			</button>
		</div>
	</div>
</template>

<i18n src="../../i18n/Details.json"></i18n>

<script lang="ts">
import Vue from "vue";
import { Component, Model, Prop } from "vue-property-decorator";

import { AbstractDetailsValueRepresentation } from "@/lib/apis/replicable/models";
import DetailsSuggestionsResourceApi from "@/lib/apis/replicable/operations/DetailsSuggestionsResourceApi";

import { Suggestions } from "@/components/form/vues/details/typings";
import { Select as NekoSelect } from "@/components/form";
import { SuggestionSelect } from "@/components/suggestionSelect";

@Component({
	components: {
		SuggestionSelect,
		NekoSelect
	}
})
export default class DetailsValueInput extends Vue {
	@Model("input", { required: true })
	public detailsValue!: AbstractDetailsValueRepresentation;

	@Prop({ default: true })
	public readonly autoValidate!: boolean;

	public suggestions: Suggestions | null = null;
	public loadingSuggestions = false;

	public async created() {
		try {
			this.suggestions = null;
			this.loadingSuggestions = true;
			let paySlipId = undefined;
			let institutionId = undefined;

			if (this.detailsValue["@type"] === "EMPLOYEE") {
				paySlipId = this.detailsValue.payslipId;
			} else if (this.detailsValue["@type"] === "INSTITUTION") {
				institutionId = this.detailsValue.institutionId;
			}

			this.suggestions = await DetailsSuggestionsResourceApi.get(
				this.detailsValue.detailsId,
				paySlipId,
				institutionId
			);
		} finally {
			this.loadingSuggestions = false;
		}
	}

	public getTypeInput(details: AbstractDetailsValueRepresentation) {
		let type = "text";

		switch (details.type) {
			case "NUMERIC":
			case "PERCENTAGE":
				type = "number";
				break;
			case "DATE":
				type = "date";
				break;
		}

		return type;
	}

	public onDelete() {
		this.$emit("delete");
	}

	public formatDateForInput(value: string | null): string {
		if (!value) {
			return "";
		}

		const [day, month, year] = value.split("/");
		return `${year}-${month}-${day}`;
	}

	public onInput(event: InputEvent | string, validate: boolean = false) {
		let value: string | null = event as string;

		if (event instanceof Event) {
			const target = event.target as HTMLInputElement;
			const detailsIsNumeric = this.detailsValue.type === "NUMERIC" || this.detailsValue.type === "PERCENTAGE";
			const detailsIsDate = this.detailsValue.type === "DATE";

			value = target.value;

			if ((detailsIsNumeric && target.valueAsNumber == null) || (detailsIsDate && target.valueAsDate == null)) {
				return;
			} else if (detailsIsDate && target.valueAsDate) {
				value = this.formatDateFromInput(target.value);
			}
		}

		this.detailsValue.source = "USER";
		this.detailsValue.value = value;

		if (validate) {
			this.onValidate();
		}
	}

	public onValidate() {
		this.$emit("input", this.detailsValue);
	}

	private formatDateFromInput(value: string): string {
		const [year, month, day] = value.split("-");
		return `${day}/${month}/${year}`;
	}
}
</script>
